import { cva } from 'class-variance-authority'
import { cn } from '~/lib/utils'
import { ClientOnly } from 'remix-utils/client-only'

import { CarouselContent, CarouselItem } from '~/components/UI/shadcn/carousel'
import CarouselContainer from '../helpers/CarouselContainer'
import Icon, { IconProps } from '~/components/Helpers/Icon'
import MarkDown from '~/components/Helpers/MarkDown'
import TertiaryLink from '~/components/UI/Buttons/TertiaryLink/TertiaryLink'

const carouselVariants = cva('', {
  variants: {
    backgroundColour: {
      'ink-blue': 'bg-blue-d text-white',
      'mid-blue': 'bg-blue-m text-white',
      'light-blue': 'bg-blue-l-60 text-blue-d',
      chartreuse: 'bg-char text-blue-d',
      'light-grey': 'bg-grey-l text-blue-d',
      white: 'bg-white text-blue-d',
      'light-pink': 'bg-pink-60 text-blue-d',
      'light-aqua': 'bg-green-60 text-blue-d',
      'light-orange': 'bg-orange-60 text-blue-d',
      'light-purple': 'bg-purple-60 text-blue-d'
    }
  },
  defaultVariants: {
    backgroundColour: 'ink-blue'
  }
})

const ContainedCarousel = ({ content }: CarouselProps) => {
  const { backgroundColour, slides, loop, showIcons } = content

  const accentColour = ['ink-blue', 'mid-blue'].includes(backgroundColour ?? '')
    ? 'chartreuse'
    : ['white', 'light-grey'].includes(backgroundColour ?? '')
      ? 'mid-blue'
      : 'ink-blue'

  const items =
    Array.isArray(slides) &&
    (slides as unknown as SlideProps[])?.map(
      (slide: SlideProps, index: number) => (
        <CarouselItem
          key={`content-${index}`}
          className='gap-xs flex flex-col items-center text-center'
        >
          <div
            className={cn(
              carouselVariants({ backgroundColour: accentColour }),
              '-mb-s -mt-s lg:-mt-l h-xl sm:h-2xl flex aspect-square -translate-y-[50%] items-center justify-center lg:h-[96px]',
              `${slide.icon ? 'p-xs lg:p-s' : ''}`
            )}
          >
            {showIcons && slide.icon ? (
              <Icon
                icon={{
                  image: slide.icon,
                  duotone: true,
                  accentColour: [
                    'ink-blue',
                    'mid-blue',
                    'chartreuse',
                    'light-grey',
                    'white'
                  ].includes(backgroundColour ?? '')
                    ? undefined
                    : (backgroundColour as IconProps['icon']['accentColour'])
                }}
                backgroundColour={accentColour}
                className='[&>img]:max-w-l [&>img]:w-full'
              />
            ) : (
              <span className='lg:text-h1-lg sm:text-h1-md text-h1-sm font-serif'>
                {index + 1}
              </span>
            )}
          </div>
          {slide.heading && <MarkDown content={'#### ' + slide.heading} />}
          {slide.copy && <MarkDown content={slide.copy} className='grow' />}

          {slide.buttons && slide.buttons[0] && (
            <div className='mt-s w-fit'>
              <TertiaryLink
                data={slide.buttons[0]}
                colour={accentColour === 'chartreuse' ? 'white' : accentColour}
              />
            </div>
          )}
        </CarouselItem>
      )
    )

  return (
    <ClientOnly>
      {() => (
        <div className='@container/containedcarousel'>
          <div className='pt-l mx-auto lg:max-w-2/3 @max-[93.5rem]/containedcarousel:max-w-full'>
            {slides && (
              <div className={cn(carouselVariants({ backgroundColour }), '')}>
                <CarouselContainer
                  className='space-y-m p-xs-plus pb-s sm:pb-m lg:p-l sm:p-[40px] lg:pb-[40px] [&>div]:!overflow-visible [&>div]:!overflow-x-clip'
                  options={{ loop, align: 'start' }}
                  controlsColour={
                    accentColour === 'chartreuse'
                      ? 'white'
                      : backgroundColour === 'chartreuse'
                        ? 'mid-blue'
                        : accentColour
                  }
                  hideControls={slides.length < 2 ? true : false}
                  controlsClasses='justify-center'
                >
                  <CarouselContent className='ml-0 [&>div]:!pl-0'>
                    {items}
                  </CarouselContent>
                </CarouselContainer>
              </div>
            )}
          </div>
        </div>
      )}
    </ClientOnly>
  )
}

export default ContainedCarousel
